import { useMemo, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'
import { ArrowLeft, X, Trash2, Pencil, TrendingUp, Check, RefreshCw, History, ChevronDown, ChevronRight } from 'lucide-react'
import { useApi } from '../hooks/useApi'
import { useAuth } from '../hooks/useAuth'
import { useData } from '../hooks/useData'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import {
  formatDate,
  formatDateTime,
  formatNumber,
  getInitials,
  statusConfig,
  calculateRealisedKPIs,
  calculateTotalForecastHrs,
} from '../lib/utils'
import { CHART_COLORS, GRID_STROKE, AXIS_TICK } from '../lib/chartOpts'
import {
  MONTH_NAMES,
  monthKey,
  getEntry,
  yearTotal,
  yearToDateTotal,
  ledgerYears,
  diffEdits,
  applyTotalEdits,
  type LedgerDiffRow,
} from '../lib/ledger'
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '../components/ui/chart'
import LoadingSpinner from '../components/LoadingSpinner'
import { ChartCard } from '../components/ChartCard'
import Field, { inputClass } from '../components/Field'
import Modal from '../components/Modal'
import UserPicker from '../components/UserPicker'
import type { Product, Project, UserRef, User, SurveyResponse, Settings, AuditEntry } from '../types'

export default function ProductDetail() {
  const api = useApi()
  const { user } = useAuth()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data, loading, refetch } = useData(() =>
    Promise.all([
      api.getProduct(id!),
      api.getProjects(),
      api.getUsers(),
      api.getSurveyResponses(),
      api.getSettings(),
    ]).then(
      ([product, projects, users, responses, settings]) => ({ product, projects, users, responses, settings }),
    ),
    [id],
  )

  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [errorModal, setErrorModal] = useState<string | null>(null)

  // Edit modal state - name/vendor/description plus owner reassignment via
  // the directory picker. Any authenticated user may edit; reassignment exists
  // so products can be transferred when an owner leaves the company.
  const [showEdit, setShowEdit] = useState(false)
  const [editName, setEditName] = useState('')
  const [editVendor, setEditVendor] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editOwner, setEditOwner] = useState<UserRef | undefined>(undefined)
  const [savingEdit, setSavingEdit] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  // Ledger is now on Project, not Product. The product page shows an
  // aggregated read-only view across linked projects. Edits target the
  // aggregate totals, then the review modal lets the user allocate
  // deltas across individual projects before saving.
  const [ledgerYear, setLedgerYear] = useState<number>(new Date().getFullYear())
  const [ledgerEditing, setLedgerEditing] = useState(false)
  const [editBuffer, setEditBuffer] = useState<Record<string, number>>({})
  const [ledgerReview, setLedgerReview] = useState<LedgerDiffRow[] | null>(null)
  const [ledgerNote, setLedgerNote] = useState('')
  const [savingLedger, setSavingLedger] = useState(false)
  const [refreshingForecast, setRefreshingForecast] = useState(false)
  const [refreshConfirm, setRefreshConfirm] = useState(false)
  // Allocation: month key → { projectId → delta hrs }
  const [allocations, setAllocations] = useState<Record<string, Record<string, number>>>({})
  const [allocationErrors, setAllocationErrors] = useState<Record<string, boolean>>({})
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set())

  // Activity / audit trail - collapsed by default. Unlike projects, products
  // have no SYSTEM-actor writes (no Asana sync), so there's no toggle needed.
  const [auditOpen, setAuditOpen] = useState(false)

  // NB: hooks (useMemo etc.) must run on every render, so we pull values off
  // `data` with safe fallbacks first and only early-return AFTER all hooks
  // have been declared.
  const typed = data as
    | {
        product: Product
        projects: Project[]
        users: User[]
        responses: SurveyResponse[]
        settings: Settings
      }
    | undefined
  const product = typed?.product
  useDocumentTitle(product?.name)
  const projects = typed?.projects ?? []
  const users = typed?.users ?? []
  const responses = typed?.responses ?? []
  const settings = typed?.settings

  // Hours are now aggregated across linked projects' ledgers (not product.monthlyLedger).
  // Build a merged ledger for read-only summary, plus resolve the selected project.
  const linkedAll = useMemo(() => projects.filter(p => product && p.productId === product.id), [projects, product])

  // Aggregate ledger: sum all linked projects' month entries into a single view.
  const aggregatedLedger = useMemo(() => {
    const merged: Record<string, { base: number; adjustments: number; adjustmentNotes: string[] }> = {}
    for (const proj of linkedAll) {
      for (const [key, entry] of Object.entries(proj.monthlyLedger || {})) {
        if (!merged[key]) merged[key] = { base: 0, adjustments: 0, adjustmentNotes: [] }
        merged[key].base += entry.base ?? 0
        merged[key].adjustments += entry.adjustments ?? 0
        if (entry.adjustmentNotes?.length) merged[key].adjustmentNotes.push(...entry.adjustmentNotes)
      }
    }
    return merged
  }, [linkedAll])

  // All completed linked projects - eligible as allocation targets even with empty ledgers.
  const completedLinked = useMemo(() => linkedAll.filter(p => p.status === 'complete'), [linkedAll])
  // Subset with actual ledger data - drives the aggregate display.
  const completedWithLedger = completedLinked.filter(p => Object.keys(p.monthlyLedger || {}).length > 0)
  const displayLedger = aggregatedLedger

  const { ytdHrs, thisYearHrs, lifetimeHrs } = useMemo(() => {
    const now = new Date()
    const curYear = now.getFullYear()
    const curMonth = now.getMonth() + 1
    const yrs = new Set<number>()
    for (const key of Object.keys(aggregatedLedger)) {
      const y = Number(key.slice(0, 4))
      if (!Number.isNaN(y)) yrs.add(y)
    }
    let lifetime = 0
    for (const y of yrs) {
      if (y < curYear) lifetime += yearTotal(aggregatedLedger, y)
      else if (y === curYear) lifetime += yearToDateTotal(aggregatedLedger, y, curMonth)
      // future years are not realised
    }
    return {
      ytdHrs: yearToDateTotal(aggregatedLedger, curYear, curMonth),
      thisYearHrs: yearTotal(aggregatedLedger, curYear),
      lifetimeHrs: lifetime,
    }
  }, [aggregatedLedger])

  // Ledger year tabs + month-by-month rows for the selected year.
  const years = ledgerYears(displayLedger)
  const activeYear = years.includes(ledgerYear) ? ledgerYear : years[years.length - 1]
  const monthRows = useMemo(() => {
    const now = new Date()
    const nowYear = now.getFullYear()
    const nowMonth = now.getMonth() + 1
    return Array.from({ length: 12 }, (_, i) => {
      const m = i + 1
      const key = monthKey(activeYear, m)
      const entry = getEntry(displayLedger, key)
      const savedTotal = entry.base + entry.adjustments
      const edited = editBuffer[key]
      const currentTotal = edited !== undefined ? edited : savedTotal
      const isActual = activeYear < nowYear || (activeYear === nowYear && m <= nowMonth)
      return {
        key,
        label: MONTH_NAMES[i],
        base: entry.base,
        deltaVsBase: currentTotal - entry.base,
        total: currentTotal,
        notes: entry.adjustmentNotes,
        dirty: edited !== undefined && Math.round(edited) !== Math.round(savedTotal),
        isActual,
      }
    })
  }, [displayLedger, activeYear, editBuffer])

  if (loading || !data || !product || !settings) return <LoadingSpinner />

  // Resolve display name/email from the live directory keyed on oid. Falls
  // back to the snapshot embedded on the product doc if the owner hasn't
  // been seen by the directory yet (e.g. just-created user, mock mode).
  const usersByOid = new Map(users.map(u => [u.oid, u]))
  const liveOwner = usersByOid.get(product.ownerRef.oid)
  const ownerName = liveOwner?.name || product.ownerRef.name
  const ownerEmail = liveOwner?.email || product.ownerRef.email

  // Roll-up math - products are a read-only view over their linked projects.
  const completeLinked = linkedAll.filter(p => p.status === 'complete')
  const forecastLinked = linkedAll.filter(p => p.status === 'forecast')
  const linkedIds = new Set(linkedAll.map(p => p.id))
  const scopedResponses = responses.filter(r => linkedIds.has(r.projectId))

  // Pipeline hours = forecast projects' expectedValue + remaining ledger months
  // on completed linked projects. Shared with Dashboard/Reports via
  // calculateTotalForecastHrs so "forecast" means the same thing everywhere.
  const pipelineHrs = calculateTotalForecastHrs(completeLinked, forecastLinked)
  const kpis = calculateRealisedKPIs(completeLinked, scopedResponses, settings.onTimeDays, settings.atCostPct)

  const activeYearTotal = monthRows.reduce((s, r) => s + r.total, 0)

  // Monthly trend chart - one point per month for the active year, showing
  // the total (base + adjustments). Replaces the old per-quarter bar chart.
  const trendData = monthRows.map(r => ({ month: r.label, hrs: Math.round(r.total) }))
  const trendConfig: ChartConfig = { hrs: { label: 'Hrs Saved', color: CHART_COLORS.cyan } }

  const isOwner = user?.id === product.ownerRef.oid
  const canEditLedger = completedLinked.length > 0

  const handleDelete = async () => {
    try {
      await api.deleteProduct(product.id)
      navigate('/products')
    } catch (e: any) {
      setErrorModal(e?.message || 'Failed to delete product')
    }
  }

  const openEdit = () => {
    setEditName(product.name)
    setEditVendor(product.vendor || '')
    setEditDescription(product.description || '')
    setEditOwner(product.ownerRef)
    setEditError(null)
    setShowEdit(true)
  }

  // ─── Ledger edit handlers ───────────────────────────────────────────────
  // Edits target the aggregate totals. On review, the user allocates
  // deltas across individual projects before confirming.
  const startLedgerEdit = () => {
    if (completedLinked.length === 0) return
    setEditBuffer({})
    setAllocations({})
    setAllocationErrors({})
    setLedgerEditing(true)
  }

  const cancelLedgerEdit = () => {
    setEditBuffer({})
    setLedgerEditing(false)
    setLedgerReview(null)
    setLedgerNote('')
    setAllocations({})
    setAllocationErrors({})
    setExpandedMonths(new Set())
  }

  const setTotalFor = (key: string, raw: string) => {
    if (raw === '' || raw === '-') { setEditBuffer(b => ({ ...b, [key]: 0 })); return }
    const n = Number(raw)
    if (Number.isFinite(n)) setEditBuffer(b => ({ ...b, [key]: n }))
  }

  const computeDefaultAllocations = (diff: LedgerDiffRow[]) => {
    const result: Record<string, Record<string, number>> = {}
    for (const row of diff) {
      const monthAlloc: Record<string, number> = {}
      if (completedLinked.length === 1) {
        monthAlloc[completedLinked[0].id] = row.delta
      } else {
        // Proportional split based on each project's current total for this month
        const totals = completedLinked.map(p => {
          const e = getEntry(p.monthlyLedger, row.key)
          return Math.abs(e.base + e.adjustments)
        })
        const sum = totals.reduce((a, b) => a + b, 0)
        if (sum === 0) {
          // Equal split
          const base = Math.floor(row.delta / completedLinked.length)
          const remainder = Math.round(row.delta - base * completedLinked.length)
          completedLinked.forEach((p, i) => {
            monthAlloc[p.id] = base + (i < remainder ? 1 : 0)
          })
        } else {
          let allocated = 0
          completedLinked.forEach((p, i) => {
            if (i === completedLinked.length - 1) {
              monthAlloc[p.id] = Math.round(row.delta - allocated)
            } else {
              const share = Math.round(row.delta * (totals[i] / sum))
              monthAlloc[p.id] = share
              allocated += share
            }
          })
        }
      }
      result[row.key] = monthAlloc
    }
    return result
  }

  const openReview = () => {
    const diff = diffEdits(aggregatedLedger, editBuffer)
    if (diff.length === 0) { setLedgerEditing(false); setEditBuffer({}); return }
    setLedgerReview(diff)
    setAllocations(computeDefaultAllocations(diff))
    setAllocationErrors({})
    // Auto-expand all months when ≤4 changes; collapse when many
    setExpandedMonths(diff.length <= 4 ? new Set(diff.map(r => r.key)) : new Set())
  }

  const setAllocationFor = (mk: string, projectId: string, value: number) => {
    setAllocations(prev => ({
      ...prev,
      [mk]: { ...prev[mk], [projectId]: value },
    }))
  }

  const confirmLedgerSave = async () => {
    if (!ledgerReview) return
    // Validate allocations sum to delta for each month
    const errors: Record<string, boolean> = {}
    for (const row of ledgerReview) {
      const monthAlloc = allocations[row.key] || {}
      const sum = Object.values(monthAlloc).reduce((a, b) => a + b, 0)
      if (Math.round(sum) !== Math.round(row.delta)) errors[row.key] = true
    }
    if (Object.keys(errors).length > 0) { setAllocationErrors(errors); return }

    setSavingLedger(true)
    try {
      // Build per-project edit maps from allocations
      const projectEdits = new Map<string, Record<string, number>>()
      for (const row of ledgerReview) {
        const monthAlloc = allocations[row.key] || {}
        for (const proj of completedLinked) {
          const delta = monthAlloc[proj.id] ?? 0
          if (delta === 0) continue
          if (!projectEdits.has(proj.id)) projectEdits.set(proj.id, {})
          const projEntry = getEntry(proj.monthlyLedger, row.key)
          projectEdits.get(proj.id)![row.key] = projEntry.base + projEntry.adjustments + delta
        }
      }
      // Save each project in parallel
      await Promise.all(
        Array.from(projectEdits.entries()).map(([projId, edits]) => {
          const proj = completedLinked.find(p => p.id === projId)!
          const nextLedger = applyTotalEdits(proj.monthlyLedger, edits, ledgerNote)
          return api.updateProject(projId, { monthlyLedger: nextLedger })
        })
      )
      setLedgerReview(null)
      setLedgerEditing(false)
      setEditBuffer({})
      setLedgerNote('')
      setAllocations({})
      setAllocationErrors({})
      setExpandedMonths(new Set())
      refetch()
    } catch (e: any) {
      setErrorModal(e?.message || 'Failed to save ledger changes')
    }
    setSavingLedger(false)
  }

  const runRefreshForecast = async () => {
    if (completedWithLedger.length === 0) return
    setRefreshingForecast(true)
    try {
      await Promise.all(completedWithLedger.map(p => api.refreshForecastYears(p.id)))
      // Refresh stays scoped to projects that have ledger data - nothing to refresh on empty ledgers.
      setRefreshConfirm(false)
      refetch()
    } catch (e: any) {
      setRefreshConfirm(false)
      setErrorModal(e?.message || 'Failed to refresh forecast years')
    }
    setRefreshingForecast(false)
  }

  const handleSaveEdit = async () => {
    const name = editName.trim()
    if (!name) {
      setEditError('Name is required')
      return
    }
    if (!editOwner) {
      setEditError('Owner is required')
      return
    }
    setSavingEdit(true)
    setEditError(null)
    try {
      // The picker hands us a UserRef with the canonical Entra oid - no
      // synthesised identifiers, no email-as-oid fallback. If the picked
      // user later gets renamed in Entra, the next sign-in updates their
      // directory entry and the product header re-renders with the new name.
      await api.updateProduct(product.id, {
        name,
        vendor: editVendor.trim() || undefined,
        description: editDescription.trim() || undefined,
        ownerRef: editOwner,
      })
      setShowEdit(false)
      refetch()
    } catch (e: any) {
      setEditError(e?.message || 'Failed to save changes')
    }
    setSavingEdit(false)
  }

  return (
    <div>
      <Modal open={!!errorModal} title="Error" message={errorModal ?? ''} variant="danger" onConfirm={() => setErrorModal(null)} />
      <Modal
        open={deleteConfirm}
        title="Delete Product"
        message={`Delete "${product.name}"? Linked projects will keep their reference but the product page will be gone. This cannot be undone.`}
        variant="danger"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={() => { setDeleteConfirm(false); handleDelete() }}
        onCancel={() => setDeleteConfirm(false)}
      />
      <Modal
        open={refreshConfirm}
        title="Refresh forecast years?"
        message={
          "This rewrites the forecast for the current and future years based on the last full year of data. " +
          "Your manual adjustments on forecast months stay. Historic years (before last year) are not touched."
        }
        confirmLabel={refreshingForecast ? 'Refreshing…' : 'Refresh'}
        cancelLabel="Cancel"
        onConfirm={runRefreshForecast}
        onCancel={() => setRefreshConfirm(false)}
      />

      <Link to="/products" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Products
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-slate-800">{product.name}</h1>
          <p className="text-slate-500 text-sm mt-1 flex items-center gap-2 flex-wrap">
            {product.vendor && (
              <span className="inline-block px-2.5 py-0.5 bg-slate-100 text-slate-600 text-xs font-semibold rounded-full">{product.vendor}</span>
            )}
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
              <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-[9px]">
                {getInitials(ownerEmail || ownerName || 'U')}
              </span>
              {ownerName || ownerEmail}
            </span>
            {(product.contributors || []).length > 0 && (
              <span className="text-xs text-slate-400">+ {product.contributors.length} contributor{product.contributors.length !== 1 ? 's' : ''}</span>
            )}
          </p>
          {product.description && <p className="text-sm text-slate-600 mt-2 max-w-2xl">{product.description}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={openEdit}
            className="text-slate-500 hover:text-slate-800 px-2 py-2 rounded-lg text-sm"
            title="Edit product"
          >
            <Pencil className="w-4 h-4" />
          </button>
          {isOwner && (
            <button
              onClick={() => setDeleteConfirm(true)}
              className="text-red-400 hover:text-[#EF3340] px-2 py-2 rounded-lg text-sm"
              title="Delete product"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Summary tiles - roll-up across linked projects */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Hrs Saved YTD</div>
          <div className="text-2xl font-bold text-slate-800 mt-1">{formatNumber(Math.round(ytdHrs))}</div>
          <div className="text-[11px] text-slate-500 mt-1">
            {formatNumber(Math.round(thisYearHrs))} forecast for {new Date().getFullYear()}
            {lifetimeHrs > thisYearHrs && (
              <> · {formatNumber(Math.round(lifetimeHrs))} lifetime</>
            )}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Hrs Pipeline</div>
          <div className="text-2xl font-bold text-slate-800 mt-1">{formatNumber(pipelineHrs)}</div>
          <div className="text-[11px] text-slate-500 mt-1">across {forecastLinked.length} forecast project{forecastLinked.length !== 1 ? 's' : ''}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Avg Satisfaction</div>
          <div className="text-2xl font-bold text-slate-800 mt-1">
            {scopedResponses.length > 0 ? `${kpis.satisfaction.toFixed(1)}/5` : '-'}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">{scopedResponses.length} survey response{scopedResponses.length !== 1 ? 's' : ''}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">On Time / In Full</div>
          <div className="text-2xl font-bold text-slate-800 mt-1">
            {completeLinked.length > 0 ? `${kpis.onTime}% / ${kpis.inFull}%` : '-'}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">delivery quality</div>
        </div>
      </div>

      {/* Monthly ledger - aggregated from linked projects. Pick a project
          to edit its ledger. When no project is selected, shows read-only
          aggregate across all linked projects. */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6">
        <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-bold text-slate-800">Monthly Hours Ledger</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Aggregated across {completedWithLedger.length} project{completedWithLedger.length !== 1 ? 's' : ''} with ledger data.{' '}
              {completedLinked.length > 1 ? 'Edit totals, then allocate changes across projects.' : 'Edit to adjust.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canEditLedger && !ledgerEditing && (
              <>
                <button
                  onClick={() => setRefreshConfirm(true)}
                  title="Re-derive forecast years from the latest prior-year total"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Refresh forecast
                </button>
                <button
                  onClick={startLedgerEdit}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200"
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit ledger
                </button>
              </>
            )}
            {ledgerEditing && (
              <>
                <button
                  onClick={cancelLedgerEdit}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200"
                >
                  Cancel
                </button>
                <button
                  onClick={openReview}
                  className="btn-primary text-white px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5"
                >
                  <Check className="w-3.5 h-3.5" /> Review changes
                </button>
              </>
            )}
          </div>
        </div>

        {/* Year tabs */}
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-1">
            {years.map(y => (
              <button
                key={y}
                onClick={() => setLedgerYear(y)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
                  y === activeYear
                    ? 'bg-[#005EB8] text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {y}
              </button>
            ))}
          </div>
          <div className="text-xs text-slate-500">
            <span className="font-semibold text-slate-700">{formatNumber(Math.round(activeYearTotal))} hrs</span> for {activeYear}
          </div>
        </div>

        {/* Month table - simplified. One editable number per month (the
            total). "vs expected" shows the delta from the auto-seeded
            forecast as a subtle cue so owners can see how reality compares
            to the forecast without having to do the math. */}
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100">
              <th className="px-5 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wide">Month</th>
              <th className="px-5 py-2 text-right text-xs font-bold text-slate-600 uppercase tracking-wide">Hours saved</th>
              <th className="px-5 py-2 text-right text-xs font-bold text-slate-600 uppercase tracking-wide">vs expected</th>
              <th className="px-5 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wide">Last note</th>
            </tr>
          </thead>
          <tbody>
            {monthRows.map(row => {
              const displayTotal = Math.round(row.total)
              const displayDelta = Math.round(row.deltaVsBase)
              const displayBase = Math.round(row.base)
              return (
                <tr
                  key={row.key}
                  className={`border-b border-slate-50 last:border-0 ${
                    row.dirty ? 'bg-amber-50/50' : !row.isActual ? 'bg-slate-50/60' : ''
                  }`}
                >
                  <td className={`px-5 py-3 font-semibold ${row.isActual ? 'text-slate-700' : 'text-slate-400 italic'}`}>
                    {row.label}
                    {!row.isActual && <span className="ml-1.5 text-[10px] font-normal not-italic text-slate-400">forecast</span>}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">
                    {ledgerEditing ? (
                      <input
                        type="number"
                        step="1"
                        value={editBuffer[row.key] ?? displayTotal}
                        onChange={e => setTotalFor(row.key, e.target.value)}
                        className={`${inputClass} text-right w-28 inline-block`}
                      />
                    ) : (
                      <span className="text-sm font-semibold text-slate-800">
                        {displayTotal > 0 ? formatNumber(displayTotal) : <span className="text-slate-300 font-normal">-</span>}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right text-xs tabular-nums">
                    {/* Only show when there's actually a delta - otherwise too noisy. */}
                    {row.base === 0 && displayDelta === 0 ? (
                      <span className="text-slate-300">-</span>
                    ) : displayDelta === 0 ? (
                      <span className="text-slate-400">on forecast</span>
                    ) : (
                      <span
                        title={`Expected ${formatNumber(displayBase)} hrs from forecast`}
                        className={displayDelta > 0 ? 'text-[#00843D] font-medium' : 'text-[#EF3340] font-medium'}
                      >
                        {displayDelta > 0 ? '+' : ''}
                        {formatNumber(displayDelta)}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-500 max-w-xs">
                    {row.notes.length > 0 ? (
                      <span title={row.notes.join(' · ')} className="line-clamp-1">
                        {row.notes[row.notes.length - 1]}
                      </span>
                    ) : (
                      <span className="text-slate-300">-</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Monthly trend chart */}
      <div className="mb-6">
        <ChartCard
          icon={TrendingUp}
          iconGrad="from-[#005EB8] to-[#005EB8] text-white"
          title={`Monthly Hours Saved (${activeYear})`}
          subtitle="Each point is the sum of base + adjustments for that month"
        >
          {activeYearTotal === 0 ? (
            <div className="h-52 flex items-center justify-center text-sm text-slate-400">
              No hours recorded for {activeYear} yet
            </div>
          ) : (
            <ChartContainer config={trendConfig} className="aspect-auto h-52 w-full">
              <LineChart data={trendData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={GRID_STROKE} strokeDasharray="4 4" />
                <XAxis dataKey="month" tickLine={false} axisLine={false} tick={AXIS_TICK} />
                <YAxis tickLine={false} axisLine={false} tick={AXIS_TICK} tickFormatter={v => formatNumber(v)} />
                <ChartTooltip cursor={{ stroke: 'rgba(148,163,184,0.35)' }} content={<ChartTooltipContent />} />
                <Line
                  type="monotone"
                  dataKey="hrs"
                  stroke="var(--color-hrs)"
                  strokeWidth={2}
                  dot={{ r: 3, fill: 'var(--color-hrs)' }}
                />
              </LineChart>
            </ChartContainer>
          )}
        </ChartCard>
      </div>

      {/* Linked projects */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <h3 className="font-bold text-slate-800">Linked Projects</h3>
          <p className="text-xs text-slate-500 mt-0.5">Projects associated with this product</p>
        </div>
        {linkedAll.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-400">No projects linked yet</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                {['Project', 'Status', 'Go-Live', 'Hrs'].map(h => (
                  <th key={h} className="px-5 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {linkedAll.map(p => {
                // Hrs shown per row:
                //   - forecast  → `expectedValue` (what the project predicts)
                //   - complete  → `expectedValue` (what it contributed to the
                //                 ledger at seeding time). We intentionally
                //                 do NOT display `valueRealised` here even if
                //                 it's set - for product-linked projects the
                //                 ledger is the source of truth.
                const hrs = p.expectedValue
                return (
                  <tr key={p.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50">
                    <td className="px-5 py-3">
                      <Link to={`/projects/${p.id}`} className="font-semibold text-slate-700 hover:text-[#005EB8]">{p.name}</Link>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${statusConfig[p.status]?.style}`}>
                        {statusConfig[p.status]?.label}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-sm text-slate-600">
                      {p.actualGoLive ? formatDate(p.actualGoLive) : p.dueDate ? <span className="text-slate-400">Due {formatDate(p.dueDate)}</span> : <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-5 py-3 text-sm text-slate-700">
                      {hrs != null ? formatNumber(hrs) : <span className="text-slate-400">-</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Activity / audit trail - collapsed by default. Mirrors ProjectDetail
          but with no SYSTEM-actor filter because products aren't touched by
          Asana sync. Ledger changes are deliberately excluded by the backend
          denylist - that history lives per-month in adjustmentNotes instead,
          so this feed stays signal-rich (name, vendor, owner, contributors). */}
      <div className="mt-6 bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <button
          type="button"
          onClick={() => setAuditOpen(o => !o)}
          className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <History className="w-4 h-4 text-slate-400" />
            <h3 className="font-bold text-slate-800 text-sm">Activity</h3>
            <span className="text-xs text-slate-400">{(product.auditTrail || []).length} entries</span>
          </div>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${auditOpen ? 'rotate-180' : ''}`} />
        </button>
        {auditOpen && (
          <div className="border-t border-slate-100 px-6 py-4">
            {(() => {
              const entries: AuditEntry[] = (product.auditTrail || []).slice().reverse()
              if (entries.length === 0) {
                return <p className="text-sm text-slate-400 text-center py-4">No activity recorded</p>
              }
              return (
                <div className="space-y-2">
                  {entries.map((entry, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm py-2 border-b border-slate-50 last:border-0">
                      <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold text-[10px] flex-shrink-0">
                        {getInitials(entry.actor.email || entry.actor.name || 'U')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-700">
                          <span className="font-semibold">{entry.actor.name || entry.actor.email || 'Unknown'}</span>
                          {' '}changed{' '}
                          <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{entry.field}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 break-words">
                          <span className="line-through text-slate-400">{JSON.stringify(entry.before) ?? '-'}</span>
                          {' → '}
                          <span>{JSON.stringify(entry.after) ?? '-'}</span>
                        </div>
                      </div>
                      <div className="text-xs text-slate-400 flex-shrink-0">{formatDateTime(entry.at)}</div>
                    </div>
                  ))}
                </div>
              )
            })()}
          </div>
        )}
      </div>

      {/* Batch ledger review modal - shows diff rows + net delta + optional
          reason, saves in one PATCH when the owner confirms. The net-zero
          case is still shown (e.g. moving hours between months). */}
      {ledgerReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto py-8">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setLedgerReview(null)} />
          <div className={`relative bg-white rounded-2xl shadow-2xl w-full mx-4 p-6 ${completedLinked.length > 1 ? 'max-w-2xl' : 'max-w-lg'}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-slate-800">Review ledger changes</h2>
              <button onClick={() => setLedgerReview(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Confirm your changes below. They'll save together in one go.
            </p>
            {/* Summary diff table */}
            <div className="border border-slate-200 rounded-xl overflow-hidden mb-4">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                  <tr>
                    <th className="px-3 py-2 text-left font-bold">Month</th>
                    <th className="px-3 py-2 text-right font-bold">Before</th>
                    <th className="px-3 py-2 text-right font-bold">After</th>
                    <th className="px-3 py-2 text-right font-bold">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerReview.map(row => (
                    <tr key={row.key} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-semibold text-slate-700">{row.monthLabel}</td>
                      <td className="px-3 py-2 text-right text-slate-600 tabular-nums">{formatNumber(row.before)}</td>
                      <td className="px-3 py-2 text-right text-slate-800 tabular-nums font-semibold">{formatNumber(row.after)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums font-semibold ${row.delta > 0 ? 'text-[#00843D]' : row.delta < 0 ? 'text-[#EF3340]' : 'text-slate-400'}`}>
                        {row.delta > 0 ? '+' : ''}{formatNumber(row.delta)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-slate-200 bg-slate-50">
                    <td colSpan={3} className="px-3 py-2 text-right text-xs font-bold text-slate-600 uppercase tracking-wide">Net change</td>
                    {(() => {
                      const net = ledgerReview.reduce((s, r) => s + r.delta, 0)
                      return (
                        <td className={`px-3 py-2 text-right tabular-nums font-bold ${net > 0 ? 'text-[#00843D]' : net < 0 ? 'text-[#EF3340]' : 'text-slate-500'}`}>
                          {net > 0 ? '+' : ''}{formatNumber(net)}
                        </td>
                      )
                    })()}
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Allocation - accordion per changed month */}
            {completedLinked.length > 1 && (
              <div className="mb-4">
                <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">
                  Allocate across projects
                </h3>
                <p className="text-[11px] text-slate-400 mb-3">
                  Expand each month to split the change across projects.
                </p>
                <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
                  {ledgerReview.map(row => {
                    const monthAlloc = allocations[row.key] || {}
                    const allocSum = Object.values(monthAlloc).reduce((a, b) => a + b, 0)
                    const balanced = Math.round(allocSum) === Math.round(row.delta)
                    const hasError = allocationErrors[row.key]
                    const isOpen = expandedMonths.has(row.key)
                    const toggleMonth = () => setExpandedMonths(prev => {
                      const next = new Set(prev)
                      if (next.has(row.key)) next.delete(row.key); else next.add(row.key)
                      return next
                    })
                    return (
                      <div key={row.key} className={hasError ? 'bg-red-50/50' : ''}>
                        <button
                          type="button"
                          onClick={toggleMonth}
                          className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-slate-50/50 transition-colors text-sm"
                        >
                          <div className="flex items-center gap-2">
                            {isOpen
                              ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                              : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                            <span className="font-semibold text-slate-700">{row.monthLabel}</span>
                            <span className={`text-xs font-medium ${row.delta > 0 ? 'text-[#00843D]' : 'text-[#EF3340]'}`}>
                              {row.delta > 0 ? '+' : ''}{formatNumber(row.delta)}
                            </span>
                          </div>
                          <span className={`text-xs font-semibold ${balanced ? 'text-[#00843D]' : 'text-[#F88806]'}`}>
                            {balanced ? '✓ Balanced' : `${formatNumber(Math.round(row.delta - allocSum))} remaining`}
                          </span>
                        </button>
                        {isOpen && (
                          <div className="px-3 pb-3 pt-1 space-y-2 bg-slate-50/30">
                            {completedLinked.map(p => (
                              <div key={p.id} className="flex items-center gap-3">
                                <span className="flex-1 text-sm text-slate-700 truncate" title={p.name}>{p.name}</span>
                                <input
                                  type="number"
                                  step="1"
                                  value={monthAlloc[p.id] ?? 0}
                                  onChange={e => {
                                    const v = Number(e.target.value)
                                    if (Number.isFinite(v)) setAllocationFor(row.key, p.id, v)
                                  }}
                                  className={`${inputClass} text-right w-24`}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
                {Object.keys(allocationErrors).length > 0 && (
                  <p className="text-xs text-[#EF3340] mt-2">
                    Some months have unbalanced allocations. Each row must sum to its total change.
                  </p>
                )}
              </div>
            )}

            <Field label="Reason (optional)">
              <input
                type="text"
                value={ledgerNote}
                onChange={e => setLedgerNote(e.target.value)}
                placeholder="e.g. new team onboarded in March"
                className={inputClass}
              />
            </Field>
            <p className="text-[11px] text-slate-400 mt-2">
              The reason is saved against every month you've changed so the history stays clear.
            </p>
            <div className="flex gap-2 justify-end mt-6">
              <button
                onClick={() => setLedgerReview(null)}
                disabled={savingLedger}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 disabled:opacity-50"
              >
                Back
              </button>
              <button
                onClick={confirmLedgerSave}
                disabled={savingLedger}
                className="btn-primary text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
              >
                {savingLedger ? 'Saving…' : 'Confirm & save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit product modal */}
      {showEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowEdit(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-slate-800">Edit Product</h2>
              <button onClick={() => setShowEdit(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-4">
              <Field label="Name">
                <input
                  type="text"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  className={inputClass}
                  autoFocus
                />
              </Field>
              <Field label="Vendor">
                <input
                  type="text"
                  value={editVendor}
                  onChange={e => setEditVendor(e.target.value)}
                  className={inputClass}
                  placeholder="Optional"
                />
              </Field>
              <Field label="Description">
                <textarea
                  value={editDescription}
                  onChange={e => setEditDescription(e.target.value)}
                  className={inputClass}
                  rows={3}
                  placeholder="Optional"
                />
              </Field>
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Owner</div>
                <UserPicker value={editOwner} onChange={setEditOwner} placeholder="Pick an owner" required />
                <p className="text-[11px] text-slate-400 mt-1.5">
                  Pick anyone who has signed in. Name changes flow through automatically the next time they sign in.
                </p>
              </div>
              {editError && <p className="text-sm text-[#EF3340]">{editError}</p>}
            </div>
            <div className="flex gap-2 justify-end mt-6">
              <button
                onClick={() => setShowEdit(false)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={savingEdit}
                className="btn-primary text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
              >
                {savingEdit ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
